import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui";
import {
  FileText,
  Wallet,
  List,
  TrendingUp,
  Scale,
  DollarSign,
  Star,
  Clock,
  Calendar,
  Download,
  Settings,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import reportsService from "../../services/reportsService";
import {
  AccountLedger,
  TransactionList,
  BudgetReport,
  ProfitLossStatement,
  BalanceSheet,
  CashFlowStatement,
  ReportHistory,
} from "./components/reports";

/**
 * Reports Center Dashboard
 *
 * Main entry point for the Advanced Finance Reports module.
 * Displays report categories, recent reports, favorites, and scheduled reports.
 *
 * Requirements: 1.1-1.5
 */
const ReportsCenterDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    recent_reports: [],
    favorites: [],
    scheduled_reports: [],
    categories: [],
  });

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await reportsService.getDashboard();

      if (response?.data?.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error("Failed to load reports dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (categoryId) => {
    const icons = {
      account_ledger: FileText,
      budget_report: Wallet,
      transaction_list: List,
      profit_loss: TrendingUp,
      balance_sheet: Scale,
      cash_flow: DollarSign,
    };
    return icons[categoryId] || FileText;
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/finance/reports-center/${categoryId}`);
  };

  const handleRecentReportClick = (report) => {
    // Navigate to the specific report with saved parameters
    navigate(`/finance/reports-center/${report.report_type}`, {
      state: { historyId: report.id },
    });
  };

  const handleFavoriteClick = (favorite) => {
    // Load the favorite configuration and navigate to report
    navigate(`/finance/reports-center/${favorite.report_type}`, {
      state: { configId: favorite.id },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports Center</h1>
          <p className="text-gray-600 mt-1">
            Generate, analyze, and export financial reports
          </p>
        </div>
        <Button
          onClick={() => navigate("/finance/reports-center/history")}
          variant="outline"
        >
          <Clock className="h-4 w-4 mr-2" />
          View History
        </Button>
      </div>

      {/* Report Categories */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Report Categories</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {dashboardData.categories.map((category) => {
            const Icon = getCategoryIcon(category.id);
            return (
              <Card
                key={category.id}
                className="cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => handleCategoryClick(category.id)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{category.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Generate {category.name.toLowerCase()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Tabs for Recent, Favorites, Scheduled */}
      <Tabs defaultValue="recent" className="w-full">
        <TabsList>
          <TabsTrigger value="recent">
            <Clock className="h-4 w-4 mr-2" />
            Recent Reports
          </TabsTrigger>
          <TabsTrigger value="favorites">
            <Star className="h-4 w-4 mr-2" />
            Favorites
          </TabsTrigger>
          <TabsTrigger value="scheduled">
            <Calendar className="h-4 w-4 mr-2" />
            Scheduled
          </TabsTrigger>
        </TabsList>

        {/* Recent Reports */}
        <TabsContent value="recent">
          <Card>
            <CardHeader>
              <CardTitle>Recently Generated Reports</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData.recent_reports.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No recent reports. Generate your first report to get started.
                </p>
              ) : (
                <div className="space-y-3">
                  {dashboardData.recent_reports.map((report) => (
                    <div
                      key={report.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleRecentReportClick(report)}
                    >
                      <div className="flex items-center space-x-4">
                        <FileText className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="font-medium">
                            {report.Configuration?.name || report.report_type}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(report.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {report.export_format !== "none" && (
                          <Badge variant="secondary">
                            {report.export_format}
                          </Badge>
                        )}
                        <Download className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Favorites */}
        <TabsContent value="favorites">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Favorite Reports</CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    navigate("/finance/reports-center/configurations")
                  }
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Manage
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {dashboardData.favorites.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No favorite reports saved. Save a report configuration to
                  quick access.
                </p>
              ) : (
                <div className="space-y-3">
                  {dashboardData.favorites.map((favorite) => (
                    <div
                      key={favorite.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleFavoriteClick(favorite)}
                    >
                      <div className="flex items-center space-x-4">
                        <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                        <div>
                          <p className="font-medium">{favorite.name}</p>
                          <p className="text-sm text-gray-500">
                            {favorite.report_type.replace(/_/g, " ")}
                          </p>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost">
                        Generate
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scheduled Reports */}
        <TabsContent value="scheduled">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Scheduled Reports</CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate("/finance/reports-center/schedules")}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Manage
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {dashboardData.scheduled_reports.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No scheduled reports. Set up automatic report delivery.
                </p>
              ) : (
                <div className="space-y-3">
                  {dashboardData.scheduled_reports.map((schedule) => (
                    <div
                      key={schedule.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center space-x-4">
                        <Calendar className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="font-medium">
                            {schedule.Configuration?.name || "Scheduled Report"}
                          </p>
                          <p className="text-sm text-gray-500">
                            {schedule.frequency} • Next run:{" "}
                            {new Date(schedule.next_run_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <Badge
                        variant={schedule.is_active ? "success" : "secondary"}
                      >
                        {schedule.is_active ? "Active" : "Paused"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const ReportsCenter = () => {
  return (
    <Routes>
      <Route index element={<ReportsCenterDashboard />} />
      <Route path="account-ledger" element={<AccountLedger />} />
      <Route path="account_ledger" element={<AccountLedger />} />
      <Route path="transaction-list" element={<TransactionList />} />
      <Route path="transaction_list" element={<TransactionList />} />
      <Route path="budget-report" element={<BudgetReport />} />
      <Route path="budget_report" element={<BudgetReport />} />
      <Route path="profit-loss" element={<ProfitLossStatement />} />
      <Route path="profit_loss" element={<ProfitLossStatement />} />
      <Route path="balance-sheet" element={<BalanceSheet />} />
      <Route path="balance_sheet" element={<BalanceSheet />} />
      <Route path="cash-flow" element={<CashFlowStatement />} />
      <Route path="cash_flow" element={<CashFlowStatement />} />
      <Route path="history" element={<ReportHistory />} />
    </Routes>
  );
};

export default ReportsCenter;
